/**
 * CameraDrawer — "Edit body camera settings" centered modal (May 2026 Figma).
 *
 * NOTE: legacy filename retained so CamerasPage import path stays unchanged,
 * but this is now a centered modal using the shared .os-* shell (consistent
 * with Site / Talk group / Team / User / Role modals).
 *
 * Layout:
 *   ┌── Edit body camera settings ──────────────────────────────┐
 *   │ ┌── General (active) ─────┐  Body camera name             │
 *   │ │ ⚙ General              │  [ Safety Cam 3 ]              │
 *   │ │ 📶 Network settings     │  Serial number                 │
 *   │ │                         │  [ 01-23-11-10 ]              │
 *   │ │                         │  Site association              │
 *   │ │                         │  [ Site-1 ]                    │
 *   │ │                         │  Firmware version              │
 *   │ │                         │  [ FR-V.2.0 ] (read-only)     │
 *   │ └─────────────────────────┘                                │
 *   │                                  [ Cancel ]  [ Save ]      │
 *   └────────────────────────────────────────────────────────────┘
 *
 * Props:
 *   open    : boolean
 *   device  : existing device object (required for edit)
 *   onClose : () => void
 *   onSaved : () => void
 */
import { useEffect, useState } from 'react';
import deviceService from '../../services/deviceService';
import { useTenant } from '../../context/TenantContext';

const EMPTY = {
  deviceName: '',
  serialNumber: '',
  siteId: '',
  firmwareVersion: '',
  // Network settings — Wi-Fi
  wifiDhcp: true,
  wifiIpAddress: '',
  wifiSubnetMask: '',
  wifiGateway: '',
  // Network settings — Docked
  dockedNetwork: true,
  dockedIpAddress: '',
  dockedSubnetMask: '',
  dockedGateway: '',
  acquireDockedDnsAuto: true,
  dockedDnsAddress: '',
};

function formFromDevice(d) {
  if (!d) return EMPTY;
  const net = d.networkSettings || d.network || {};
  return {
    deviceName:      d.deviceName   || '',
    serialNumber:    d.serialNumber || '',
    siteId:          d.siteId != null ? String(d.siteId) : '',
    firmwareVersion: d.firmwareVersion || '',
    wifiDhcp:        net.wifiDhcp ?? true,
    wifiIpAddress:   net.wifiIpAddress   || d.ipAddress || '',
    wifiSubnetMask:  net.wifiSubnetMask  || '',
    wifiGateway:     net.wifiGateway     || '',
    dockedNetwork:   net.dockedNetwork ?? true,
    dockedIpAddress: net.dockedIpAddress || '',
    dockedSubnetMask:net.dockedSubnetMask|| '',
    dockedGateway:   net.dockedGateway   || '',
    acquireDockedDnsAuto: net.acquireDockedDnsAuto ?? true,
    dockedDnsAddress:    net.dockedDnsAddress || '',
  };
}

export default function CameraDrawer({ open, device, onClose, onSaved }) {
  const { orgId, sites } = useTenant();
  const [tab, setTab] = useState('general');
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(formFromDevice(device));
      setTab('general');
      setError(null);
      setSubmitting(false);
    }
  }, [open, device]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !device) return null;

  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const toggle = (key) => () => setForm((p) => ({ ...p, [key]: !p[key] }));

  const canSubmit =
    form.deviceName.trim().length > 0 &&
    !!orgId &&
    !submitting;

  const siteAssociationLabel = (() => {
    if (!form.siteId) return '— Unassigned —';
    const site = (sites || []).find((s) => String(s.siteId ?? s.id) === String(form.siteId));
    return site?.siteName ?? site?.name ?? device?.siteName ?? `Site ${form.siteId}`;
  })();

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await deviceService.updateDevice(device.deviceId, {
        deviceName:   form.deviceName.trim(),
        networkSettings: {
          wifiDhcp:             form.wifiDhcp,
          wifiIpAddress:        form.wifiDhcp ? (form.wifiIpAddress  || undefined) : undefined,
          wifiSubnetMask:       form.wifiDhcp ? (form.wifiSubnetMask || undefined) : undefined,
          wifiGateway:          form.wifiDhcp ? (form.wifiGateway    || undefined) : undefined,
          dockedNetwork:        form.dockedNetwork,
          dockedIpAddress:      form.dockedNetwork ? (form.dockedIpAddress  || undefined) : undefined,
          dockedSubnetMask:     form.dockedNetwork ? (form.dockedSubnetMask || undefined) : undefined,
          dockedGateway:        form.dockedNetwork ? (form.dockedGateway    || undefined) : undefined,
          acquireDockedDnsAuto: form.acquireDockedDnsAuto,
          dockedDnsAddress:     form.acquireDockedDnsAuto ? undefined : (form.dockedDnsAddress || undefined),
        },
      }, orgId);
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.message
        ?? err?.response?.data?.error
        ?? err.message
        ?? 'Failed to update body camera',
      );
      setSubmitting(false);
    }
  };

  return (
    <div
      className="os-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Edit body camera settings"
      onMouseDown={(e) => e.target === e.currentTarget && !submitting && onClose?.()}
    >
      <div className="os-modal bc-modal" onClick={(e) => e.stopPropagation()}>
        <header className="os-modal-header">
          <h2>Edit body camera settings</h2>
        </header>

        <div className="os-modal-body">
          <aside className="os-side">
            <button
              type="button"
              className={`os-side-item ${tab === 'general' ? 'active' : ''}`}
              onClick={() => setTab('general')}
            >
              <i className="fas fa-cog" />
              <span>General</span>
            </button>
            <button
              type="button"
              className={`os-side-item ${tab === 'network' ? 'active' : ''}`}
              onClick={() => setTab('network')}
            >
              <i className="fas fa-wifi" />
              <span>Network settings</span>
            </button>
          </aside>

          <section className="os-pane bc-pane">
            {tab === 'general' && (
              <div className="bc-form">
                <div className="bc-field">
                  <label className="os-label">Body camera name</label>
                  <input
                    type="text"
                    className="os-input"
                    placeholder="Body camera name"
                    value={form.deviceName}
                    onChange={set('deviceName')}
                  />
                </div>

                <div className="bc-field">
                  <label className="os-label">Serial number</label>
                  <input
                    type="text"
                    className="os-input bc-readonly"
                    placeholder="Serial number"
                    value={form.serialNumber}
                    disabled
                    readOnly
                  />
                </div>

                <div className="bc-field">
                  <label className="os-label">Site association</label>
                  <input
                    type="text"
                    className="os-input bc-readonly"
                    value={siteAssociationLabel}
                    disabled
                    readOnly
                  />
                </div>

                <div className="bc-field">
                  <label className="os-label">Firmware version</label>
                  <input
                    type="text"
                    className="os-input bc-readonly"
                    value={form.firmwareVersion}
                    disabled
                    readOnly
                  />
                </div>
              </div>
            )}

            {tab === 'network' && (
              <div className="bc-form">
                {/* Body camera name (shown at top of Network settings per Figma) */}
                <div className="bc-field">
                  <label className="os-label">Body camera name</label>
                  <input
                    type="text"
                    className="os-input"
                    placeholder="Body camera name"
                    value={form.deviceName}
                    onChange={set('deviceName')}
                  />
                </div>

                {/* ── WI-FI DHCP ─────────────────────────────────────────── */}
                <div className="bc-net-section">
                  <div className="bc-net-section-header">
                    <span className="bc-net-section-title">WI-FI DHCP</span>
                    <label className="tg-toggle-switch">
                      <input
                        type="checkbox"
                        checked={form.wifiDhcp}
                        onChange={toggle('wifiDhcp')}
                      />
                      <span className="tg-toggle-slider" />
                    </label>
                  </div>

                  {form.wifiDhcp && (
                    <div className="bc-net-fields">
                      <div className="bc-field">
                        <label className="os-label">
                          Wi-Fi IP address
                          <i
                            className="fas fa-question-circle bc-help"
                            title="IPv4 address assigned over Wi-Fi"
                          />
                        </label>
                        <input
                          type="text"
                          className="os-input"
                          placeholder="123.123.123.123"
                          value={form.wifiIpAddress}
                          onChange={set('wifiIpAddress')}
                        />
                      </div>
                      <div className="bc-field">
                        <label className="os-label">
                          Wi-Fi Subnet Mask
                          <i
                            className="fas fa-question-circle bc-help"
                            title="Wi-Fi network subnet mask"
                          />
                        </label>
                        <input
                          type="text"
                          className="os-input"
                          placeholder="123.123.123.123"
                          value={form.wifiSubnetMask}
                          onChange={set('wifiSubnetMask')}
                        />
                      </div>
                      <div className="bc-field">
                        <label className="os-label">
                          Wi-Fi Gateway
                          <i
                            className="fas fa-question-circle bc-help"
                            title="Wi-Fi default gateway"
                          />
                        </label>
                        <input
                          type="text"
                          className="os-input"
                          placeholder="123.123.123.123"
                          value={form.wifiGateway}
                          onChange={set('wifiGateway')}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* ── DOCKED NETWORK ─────────────────────────────────────── */}
                <div className="bc-net-section">
                  <div className="bc-net-section-header">
                    <span className="bc-net-section-title">DOCKED NETWORK</span>
                    <label className="tg-toggle-switch">
                      <input
                        type="checkbox"
                        checked={form.dockedNetwork}
                        onChange={toggle('dockedNetwork')}
                      />
                      <span className="tg-toggle-slider" />
                    </label>
                  </div>

                  {form.dockedNetwork && (
                    <div className="bc-net-fields">
                      <div className="bc-field">
                        <label className="os-label">
                          Docked IP Address
                          <i
                            className="fas fa-question-circle bc-help"
                            title="IPv4 address when docked"
                          />
                        </label>
                        <input
                          type="text"
                          className="os-input"
                          placeholder="123.123.123.123"
                          value={form.dockedIpAddress}
                          onChange={set('dockedIpAddress')}
                        />
                      </div>
                      <div className="bc-field">
                        <label className="os-label">
                          Docked Subnet Mask
                          <i
                            className="fas fa-question-circle bc-help"
                            title="Docked network subnet mask"
                          />
                        </label>
                        <input
                          type="text"
                          className="os-input"
                          placeholder="123.123.123.123"
                          value={form.dockedSubnetMask}
                          onChange={set('dockedSubnetMask')}
                        />
                      </div>
                      <div className="bc-field">
                        <label className="os-label">
                          Docked Gateway
                          <i
                            className="fas fa-question-circle bc-help"
                            title="Docked network default gateway"
                          />
                        </label>
                        <input
                          type="text"
                          className="os-input"
                          placeholder="123.123.123.123"
                          value={form.dockedGateway}
                          onChange={set('dockedGateway')}
                        />
                      </div>

                      <label className="bc-checkbox-row">
                        <input
                          type="checkbox"
                          checked={form.acquireDockedDnsAuto}
                          onChange={toggle('acquireDockedDnsAuto')}
                        />
                        <span>Acquire Docked DNS Automatically</span>
                      </label>

                      <div className="bc-field">
                        <label className="os-label">
                          Docked DNS Address
                          <i
                            className="fas fa-question-circle bc-help"
                            title="DNS server address when docked"
                          />
                        </label>
                        <input
                          type="text"
                          className="os-input"
                          placeholder="123.123.123.123"
                          value={form.dockedDnsAddress}
                          onChange={set('dockedDnsAddress')}
                          disabled={form.acquireDockedDnsAuto}
                          readOnly={form.acquireDockedDnsAuto}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>

        {error && <div className="os-modal-error">{error}</div>}

        <footer className="os-modal-footer">
          <button type="button" className="btn-ghost-small" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary-pill"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </footer>
      </div>
    </div>
  );
}

